import { format } from "date-fns";
import { ForwardedRef, forwardRef } from "react";

import {
  Visibility,
  renderOrderedItems,
  useOrderedSections,
} from "../../hooks";
import {
  FieldItem,
  GroupItem,
  SectionItem,
  printTitle,
  TeamItem,
} from "../../types";
import { Page, Section } from "../section-components";
import { TeamLabel } from "../shared-components/team-label";
import {
  SummaryField,
  SummaryGroup,
  SummaryGroupFieldSet,
  SummarySection,
} from "../summary-components";

export type IntakePrintContentProps = {
  formTitle?: string;
  sections: SectionItem[];
  groups: GroupItem[];
  fields: FieldItem[];
  visibility?: Visibility;
  responses: Record<string, any>;
  teamDetails: {
    name: TeamItem["name"];
    picture?: TeamItem["picture"];
  };
  completedOn?: string;
};

export const IntakePrintContent = forwardRef(
  (
    {
      formTitle,
      sections,
      groups,
      fields,
      visibility,
      responses,
      teamDetails,
      completedOn,
    }: IntakePrintContentProps,
    forwardedRef: ForwardedRef<HTMLDivElement>,
  ) => {
    const orderedSections = useOrderedSections({
      sections,
      groups,
      fields,
      visibility,
      responses,
    });

    return (
      <div hidden data-testid="intake-submission-print-content">
        <style>
          {`
          @media print {
            @page {
              margin: 20px 0px;
            }
          }
        `}
        </style>
        <div className="print:m-4 print:block" ref={forwardedRef}>
          <Page>
            {teamDetails.picture && (
              <div className="ml-[1rem]">
                <TeamLabel
                  teamName={teamDetails.name}
                  teamPicture={teamDetails.picture}
                  displayTeamName={false}
                />
              </div>
            )}
            <div className="text-h1 font-poppins font-semibold ml-[2rem] mt-[4rem]">
              {formTitle || printTitle}
            </div>
            <Section>
              {orderedSections.map(({ orderedItems, section }) => (
                <SummarySection key={section.uri} section={section}>
                  {renderOrderedItems({
                    orderedItems,
                    responses,
                    renderGroup: (props) => <SummaryGroup {...props} />,
                    renderField: (props) => <SummaryField {...props} />,
                    renderGroupFieldSet: (props) => (
                      <SummaryGroupFieldSet {...props} />
                    ),
                  })}
                </SummarySection>
              ))}
              {completedOn && (
                <div className="text-ui">
                  {`Form completed on ${format(new Date(completedOn), "dd MM yyyy")}`}
                </div>
              )}
            </Section>
          </Page>
        </div>
      </div>
    );
  },
);
